import { ChiCareClient, type TokenStore } from '@chicare/shared';

/**
 * The web app's one client instance.
 *
 * The ONLY web-specific thing here is the token store: localStorage in a
 * browser, AsyncStorage in React Native. Everything else — endpoints, error
 * shapes, idempotency, the envelope — lives in @chicare/shared and is shared
 * verbatim with the mobile app. That is the point of the split.
 */

const TOKEN_KEY = 'chicare.token';

/**
 * localStorage throws in Safari private mode and in some embedded WebViews, so
 * every access is guarded. A buyer whose browser blocks storage should still be
 * able to browse; they just sign in again next visit.
 */
class BrowserTokenStore implements TokenStore {
  get(): string | null {
    try {
      return window.localStorage.getItem(TOKEN_KEY);
    } catch {
      return null;
    }
  }

  set(token: string | null): void {
    try {
      if (token === null) {
        window.localStorage.removeItem(TOKEN_KEY);
      } else {
        window.localStorage.setItem(TOKEN_KEY, token);
      }
    } catch {
      /* Storage unavailable. The session lives for this page load only. */
    }
  }
}

export const tokenStore = new BrowserTokenStore();

export const api = new ChiCareClient({
  baseUrl: import.meta.env.VITE_API_URL ?? 'http://localhost:8000/api/v1',
  tokenStore,
  onUnauthenticated: () => {
    // The token was rejected. Drop it and let the route guards react rather
    // than hard-redirecting from inside a data fetch, which would throw the
    // buyer off a page mid-scroll.
    window.dispatchEvent(new CustomEvent('chicare:unauthenticated'));
  },
});
